import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

// Dark theme styles
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
  },
  modal: {
    background: '#0B1C34',
    border: '1px solid rgba(86, 141, 237, 0.2)',
    borderRadius: '12px',
    width: '700px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
    color: '#FFFFFF',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(86, 141, 237, 0.15)',
  },
  title: {
    margin: 0,
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '20px',
    fontWeight: 600,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '24px',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  body: {
    padding: '24px',
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    marginBottom: 8,
    fontSize: '14px',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    minHeight: '200px',
    padding: '12px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  hint: {
    marginTop: '4px',
    fontFamily: 'Montserrat',
    fontSize: '12px',
    color: 'rgba(255,255,255,0.5)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid rgba(86, 141, 237, 0.15)',
  },
  cancelBtn: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 20px',
    background: '#568ded',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const EmailComposer = ({ employeeId, companyUID, employeeName, employeeEmail, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [to, setTo] = useState(employeeEmail || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!to.trim()) {
      alert('Please enter a recipient email address');
      return;
    }
    if (!subject.trim()) {
      alert('Please enter a subject');
      return;
    }
    if (!body.trim()) {
      alert('Please enter a message body');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/employee/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          to: to.trim(),
          subject: subject.trim(),
          body: body.trim(),
          cc: cc.trim() || undefined,
          bcc: bcc.trim() || undefined,
          fromUID: currentUser?.uid,
          fromName: currentUser?.displayName || currentUser?.email || 'Manager',
          fromEmail: currentUser?.email
        })
      });

      const result = await response.json();
      if (result.success) {
        alert('Email sent successfully');
        onSuccess?.();
        onClose();
      } else {
        alert('Failed to send email: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Send email failed:', error);
      alert('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const isDisabled = sending || !to.trim() || !subject.trim() || !body.trim();

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Send Email</h2>
          <button style={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <div style={styles.body}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              To <span style={{ color: '#EB4040' }}>*</span>
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="employee@example.com"
              style={styles.input}
            />
            {employeeName && (
              <div style={styles.hint}>{employeeName}</div>
            )}
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>CC (Optional)</label>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="cc@example.com"
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>BCC (Optional)</label>
            <input
              type="text"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder="bcc@example.com"
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Subject <span style={{ color: '#EB4040' }}>*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              Message <span style={{ color: '#EB4040' }}>*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message here..."
              style={styles.textarea}
            />
          </div>
        </div>
        <div style={styles.footer}>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              ...styles.cancelBtn,
              opacity: sending ? 0.5 : 1,
              cursor: sending ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isDisabled}
            style={{
              ...styles.submitBtn,
              background: isDisabled ? 'rgba(86, 141, 237, 0.4)' : '#568ded',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
